import "moment-timezone";

import {
  ApiTokenListResponse,
  CalendarEventOccurrence,
  EventOccurrenceDTO,
  EventRsvpFeeInvoiceDTO,
  MemberBookingDTO,
} from "@gymflow/types";
import { AxiosInstance } from "axios";

import { trimFields } from "./features/trimFiels";

export type EventApiType = ReturnType<typeof eventApi>;

const eventApi = (axiosInstance: AxiosInstance, clubId: number) => {
  const clubPrefix = `club/${clubId}/`;
  const fieldsToTrim = [
    "name",
    "userEventHostId",
    "startDate",
    "endDate",
    "startTime",
    "endTime",
    "startRecurringDate",
    "endRecurringDate",
    "recurrenceType",
  ];

  return {
    findById(id: number) {
      return axiosInstance.get<EventOccurrenceDTO>(
        `${clubPrefix}event/occurrence/${id}`,
      );
    },
    create(
      newResource: Record<string, any>,
      ignoreAvailabilityValidation: boolean,
    ) {
      const trimmedData = trimFields(newResource, fieldsToTrim);

      return axiosInstance.post<EventOccurrenceDTO>(
        `${clubPrefix}event/occurrence`,
        {
          event: { ...trimmedData, clubId },
          ignoreAvailabilityValidation,
        },
      );
    },
    update(
      id: number,
      patchedFields: unknown,
      isUpdateAll?: boolean,
      ignoreAvailabilityValidation?: boolean,
    ) {
      return axiosInstance.put<EventOccurrenceDTO>(
        `${clubPrefix}event/occurrence/${id}`,
        {
          event: { ...trimFields(patchedFields, fieldsToTrim), clubId },
          isAll: isUpdateAll,
          ignoreAvailabilityValidation,
        },
      );
    },
    addAttendeeToRsvp(userMemberId: string, occurrenceId: number) {
      return axiosInstance.put(
        `${clubPrefix}user-member/${userMemberId}/event-rsvp`,
        {
          occurrenceId,
        },
      );
    },
    cancelRsvp(rsvpId: number) {
      return axiosInstance.put(`${clubPrefix}event-rsvp/${rsvpId}/cancel`);
    },
    lateCancelRsvp(rsvpId: number) {
      return axiosInstance.put<EventRsvpFeeInvoiceDTO>(
        `${clubPrefix}event-rsvp/${rsvpId}/late-cancel`,
      );
    },
    noShowRsvp(rsvpId: number) {
      return axiosInstance.put<EventRsvpFeeInvoiceDTO>(
        `${clubPrefix}event-rsvp/${rsvpId}/no-show`,
      );
    },
    checkInRsvp(rsvpId: number) {
      return axiosInstance.put(`${clubPrefix}event-rsvp/${rsvpId}/check-in`);
    },
    uncheckRsvp(rsvpId: number) {
      return axiosInstance.put(`${clubPrefix}event-rsvp/${rsvpId}/uncheck`);
    },
    bookRsvp({
      rsvpId,
      ignoreCapacityCheck,
    }: {
      rsvpId: number;
      ignoreCapacityCheck: boolean;
    }) {
      return axiosInstance.put(`${clubPrefix}event-rsvp/${rsvpId}/book`, {
        ignoreCapacityCheck,
      });
    },
    delete(occurrenceId: number, deleteAll: boolean) {
      return axiosInstance.delete(
        `${clubPrefix}event/occurrence/${occurrenceId}`,
        {
          params: { isAll: deleteAll },
        },
      );
    },
    checkInAll(occurrenceId: number) {
      return axiosInstance.put(
        `${clubPrefix}event-occurrence/${occurrenceId}/mark-all-attended`,
      );
    },
    upcomingByUserMemberId({
      memberId,
      limit,
      nextPageToken,
    }: {
      readonly memberId?: string;
      readonly limit: number;
      readonly nextPageToken?: string;
    }) {
      return axiosInstance.get<ApiTokenListResponse<MemberBookingDTO>>(
        `${clubPrefix}user-member-booking/${memberId}/upcoming`,
        {
          params: {
            limit,
            nextPageToken,
            dateFrom: new Date().toISOString().split(".")[0] + "Z",
          },
        },
      );
    },
    pastByUserMemberId({
      memberId,
      limit,
      nextPageToken,
    }: {
      readonly memberId?: string;
      readonly limit: number;
      readonly nextPageToken?: string;
    }) {
      return axiosInstance.get<ApiTokenListResponse<MemberBookingDTO>>(
        `${clubPrefix}user-member-booking/${memberId}/past`,
        {
          params: {
            limit,
            nextPageToken,
            dateTo: new Date().toISOString().split(".")[0] + "Z",
          },
        },
      );
    },
    calendarEventOccurrences({
      filters,
    }: {
      filters: {
        dateFrom: string;
        dateTo: string;
        limit: number;
        nextPageToken?: string;
        includeBookedCounts?: boolean;
        includeWaitingCounts?: boolean;
        eventHostId?: string[];
        facilityId?: number[];
        activityId?: number[];
        isPublicEvent?: boolean;
        activityCategoryId?: number[];
      };
    }) {
      return axiosInstance.get<ApiTokenListResponse<CalendarEventOccurrence>>(
        `${clubPrefix}calendar/event-occurrences`,
        {
          params: filters,
        },
      );
    },
    calendarAppointments({
      filters,
    }: {
      filters: {
        dateFrom: string;
        dateTo: string;
        limit: number;
        nextPageToken?: string;
        appointmentHostId?: string[];
        appointableId?: number[];
        appointmentFacilityId?: number[];
        appointableCategoryIds?: number[];
      };
    }) {
      return axiosInstance.get(`${clubPrefix}calendar/appointments`, {
        params: filters,
      });
    },
  };
};

export const PARAMETER_DATE_ONLY_FORMAT = "YYYY-MM-DD";
export const PARAMETER_DATE_FORMAT = "YYYY-MM-DDTHH:mm:ss[Z]";
export const PARAMETER_DATE_FORMAT_WITHOUT_TZ = "YYYY-MM-DDTHH:mm:ss";
export const PARAMETER_TIME_FORMAT = "HH:mm:ss";

export default eventApi;
